import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import dayjs from "dayjs";
import {
  FolderOpen,
  Clock,
  AlertCircle,
  ChevronRight,
  ChevronDown,
  Loader,
  ClipboardList,
  Wrench,
  PanelRightClose,
  PanelRightOpen,
} from "lucide-react";
import { useGetBookings } from "~/services/booking/booking.queries";
import { useGetTasks } from "~/services/task/task.queries";
import { useGetServiceTickets } from "~/services/service-ticket/service-ticket.queries";
import { JobStatus, TaskStatus } from "~/utils/constants";
import { TBooking } from "~/types/booking";
import { TServiceTicket } from "~/types/tickets";
import { displayDateTime } from "~/utils/date-timezone.utils";

const OPEN_STATUSES = new Set([
  JobStatus.ESTIMATED,
  JobStatus.SCHEDULED,
  JobStatus.IN_PROGRESS,
]);

type SidebarServiceTicket = {
  id: string;
  serial: string;
  serviceType: string;
  status: JobStatus;
  startDatetime?: string;
};

type SidebarBooking = {
  id: string;
  referenceNumber: string;
  status: JobStatus;
  bookingDate: Date;
  clientName?: string;
  openServiceTickets: SidebarServiceTicket[];
};

type ProjectGroup = {
  projectKey: string;
  projectName: string;
  bookings: SidebarBooking[];
};

export function ActivitySidebar() {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(true);
  const [collapsedProjects, setCollapsedProjects] = useState<Set<string>>(
    new Set()
  );
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(
    new Set()
  );
  const toggleSection = (section: string) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(section)) next.delete(section);
      else next.add(section);
      return next;
    });
  };

  // Fetch scheduled + in-progress bookings
  const { data: scheduledBookings, isLoading: loadingScheduled } =
    useGetBookings({
      bookingStatus: JobStatus.SCHEDULED,
      pageSize: 50,
    });

  const { data: inProgressBookings, isLoading: loadingInProgress } =
    useGetBookings({
      bookingStatus: JobStatus.IN_PROGRESS,
      pageSize: 50,
    });

  // Fetch active tasks (PENDING + IN_PROGRESS)
  const { data: pendingTasks, isLoading: loadingPendingTasks } = useGetTasks({
    status: TaskStatus.PENDING,
    isArchived: false,
    pageSize: 100,
    sortByCreatedAt: "ASC" as any,
  });

  const { data: inProgressTasks, isLoading: loadingInProgressTasks } =
    useGetTasks({
      status: TaskStatus.IN_PROGRESS,
      isArchived: false,
      pageSize: 100,
      sortByCreatedAt: "ASC" as any,
    });

  // Fetch service ticket counts by status
  const { data: estimatedSTs } = useGetServiceTickets(
    { status: JobStatus.ESTIMATED, pageSize: 1 },
    true
  );
  const { data: scheduledSTs } = useGetServiceTickets(
    { status: JobStatus.SCHEDULED, pageSize: 1 },
    true
  );
  const { data: inProgressSTs } = useGetServiceTickets(
    { status: JobStatus.IN_PROGRESS, pageSize: 1 },
    true
  );

  // Completed within 5 days
  const fiveDaysAgo = dayjs().subtract(5, "day").format("YYYY-MM-DD");
  const today = dayjs().format("YYYY-MM-DD");
  const { data: completedSTs } = useGetServiceTickets(
    {
      status: JobStatus.COMPLETED,
      startDate: fiveDaysAgo,
      endDate: today,
      pageSize: 1,
    },
    true
  );

  // Group bookings by project name (projectId isn't returned by the list query)
  const projectGroups = useMemo(() => {
    const allBookings: TBooking[] = [
      ...(scheduledBookings?.items ?? []),
      ...(inProgressBookings?.items ?? []),
    ];

    const groupMap = new Map<string, ProjectGroup>();

    for (const booking of allBookings) {
      const pName = booking.project?.name;
      if (!pName) continue;

      if (!groupMap.has(pName)) {
        groupMap.set(pName, {
          projectKey: pName,
          projectName: pName,
          bookings: [],
        });
      }

      const openSTs = (booking.serviceTicket ?? [])
        .filter((st: TServiceTicket) => OPEN_STATUSES.has(st.status))
        .map((st: TServiceTicket) => ({
          id: st.id,
          serial: st.serial,
          serviceType: st.serviceType,
          status: st.status,
          startDatetime: st.startDatetime as any,
        }));

      groupMap.get(pName)!.bookings.push({
        id: booking.id,
        referenceNumber: booking.referenceNumber,
        status: booking.status,
        bookingDate: booking.bookingDate,
        clientName:
          booking.client?.companyName ?? booking.tempClientData?.name,
        openServiceTickets: openSTs,
      });
    }

    // Sort bookings within each group by date
    for (const group of groupMap.values()) {
      group.bookings.sort(
        (a, b) =>
          new Date(a.bookingDate).getTime() -
          new Date(b.bookingDate).getTime()
      );
    }

    return Array.from(groupMap.values()).sort((a, b) =>
      a.projectName.localeCompare(b.projectName)
    );
  }, [scheduledBookings, inProgressBookings]);

  // Start all projects collapsed on first load
  const initialCollapseApplied = useRef(false);
  useEffect(() => {
    if (!initialCollapseApplied.current && projectGroups.length > 0) {
      setCollapsedProjects(new Set(projectGroups.map((g) => g.projectKey)));
      initialCollapseApplied.current = true;
    }
  }, [projectGroups]);

  // Combine and sort tasks - oldest first, take 5 (exclude dispatch notes)
  const { activeTasks, activeTaskCount } = useMemo(() => {
    const all = [
      ...(pendingTasks?.items ?? []),
      ...(inProgressTasks?.items ?? []),
    ].filter((task) => task.taskType !== "DISPATCH_NOTE");

    const sorted = all
      .sort(
        (a, b) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      )
      .slice(0, 5);

    return { activeTasks: sorted, activeTaskCount: all.length };
  }, [pendingTasks, inProgressTasks]);

  const stCounts = {
    estimated: estimatedSTs?.meta?.totalItems ?? 0,
    scheduled: scheduledSTs?.meta?.totalItems ?? 0,
    inProgress: inProgressSTs?.meta?.totalItems ?? 0,
    completed: completedSTs?.meta?.totalItems ?? 0,
  };

  const isLoadingBookings = loadingScheduled || loadingInProgress;
  const isLoadingTasks = loadingPendingTasks || loadingInProgressTasks;

  const isActiveBooking = (bookingId: string) =>
    location.pathname === `/bookings/${bookingId}`;

  const toggleProject = (projectKey: string) => {
    setCollapsedProjects((prev) => {
      const next = new Set(prev);
      if (next.has(projectKey)) {
        next.delete(projectKey);
      } else {
        next.add(projectKey);
      }
      return next;
    });
  };

  // Collapsed state — just show the open button
  if (!isOpen) {
    return (
      <div className="hidden lg:flex shrink-0 border-l border-gray-200 bg-gray-50">
        <button
          onClick={() => setIsOpen(true)}
          className="p-2 mt-2 mx-1 text-gray-400 hover:text-gray-600 transition-colors"
          title="Open sidebar"
        >
          <PanelRightOpen size={18} />
        </button>
      </div>
    );
  }

  return (
    <div
      className="hidden lg:block w-[322px] shrink-0 border-l border-gray-200 bg-gray-50 overflow-y-auto h-[calc(100vh-4rem)] sticky top-16"
      onClick={(e) => {
        if (e.target === e.currentTarget) setIsOpen(false);
      }}
    >
      {/* Close button */}
      <div className="flex justify-end px-3 pt-3 pb-0">
        <button
          onClick={() => setIsOpen(false)}
          className="text-gray-400 hover:text-gray-600 transition-colors"
          title="Close sidebar"
        >
          <PanelRightClose size={18} />
        </button>
      </div>

      <div
        className="px-4 pb-4 space-y-5"
        onClick={(e) => {
          if (e.target === e.currentTarget) setIsOpen(false);
        }}
      >
        {/* SECTION 1: Active Projects */}
        <section>
          <div className="flex items-center gap-2 mb-3">
            <FolderOpen size={16} className="text-blue-600" />
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Active Projects
            </h3>
          </div>

          {isLoadingBookings ? (
            <div className="flex items-center gap-2 text-xs text-gray-400 py-2">
              <Loader size={14} className="animate-spin" />
              Loading...
            </div>
          ) : projectGroups.length === 0 ? (
            <p className="text-xs text-gray-400 py-1">
              No active project bookings
            </p>
          ) : (
            <div className="space-y-3">
              {projectGroups.map((group) => {
                const isCollapsed = collapsedProjects.has(group.projectKey);

                return (
                  <div key={group.projectKey}>
                    <button
                      onClick={() => toggleProject(group.projectKey)}
                      className="flex items-center gap-1 w-full text-left text-xs font-medium text-gray-700 mb-1 hover:text-gray-900 transition-colors"
                    >
                      <ChevronDown
                        size={14}
                        className={`shrink-0 text-gray-400 transition-transform ${
                          isCollapsed ? "-rotate-90" : ""
                        }`}
                      />
                      <span className="truncate">{group.projectName}</span>
                      <span className="text-[10px] text-gray-400 ml-auto shrink-0">
                        {group.bookings.length}
                      </span>
                    </button>

                    {!isCollapsed && (
                      <div className="space-y-1 pl-1">
                        {group.bookings.map((booking) => (
                          <BookingItem
                            key={booking.id}
                            booking={booking}
                            isActive={isActiveBooking(booking.id)}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>

        <hr className="border-gray-200 cursor-pointer" onClick={() => setIsOpen(false)} />

        {/* SECTION 2: Active Tasks */}
        <section>
          <button
            onClick={() => toggleSection("tasks")}
            className="flex items-center justify-between mb-3 w-full hover:opacity-70 transition-opacity"
          >
            <div className="flex items-center gap-2">
              <ChevronDown
                size={14}
                className={`text-gray-400 transition-transform ${collapsedSections.has("tasks") ? "-rotate-90" : ""}`}
              />
              <ClipboardList size={16} className="text-amber-600" />
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Active Tasks
              </h3>
            </div>
            {!isLoadingTasks && (
              <span className="text-xs font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">
                {activeTaskCount}
              </span>
            )}
          </button>

          {!collapsedSections.has("tasks") && (
            <>
              {isLoadingTasks ? (
                <div className="flex items-center gap-2 text-xs text-gray-400 py-2">
                  <Loader size={14} className="animate-spin" />
                  Loading...
                </div>
              ) : activeTasks.length === 0 ? (
                <p className="text-xs text-gray-400 py-1">No active tasks</p>
              ) : (
                <div className="space-y-1">
                  {activeTasks.map((task) => (
                    <Link
                      key={task.id}
                      to="/dispatch/internal-events"
                      className="block px-2 py-1.5 rounded text-xs bg-white border border-gray-100 hover:border-gray-300 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-start gap-1.5">
                        <TaskStatusIcon status={task.taskStatus} />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-700 truncate">
                            {task.title}
                          </p>
                          <div className="flex items-center gap-2 mt-0.5 text-[10px] text-gray-400">
                            {task.dueDate && (
                              <span>
                                Due {dayjs(task.dueDate).format("MMM D")}
                              </span>
                            )}
                            {task.taskRef && <span>{task.taskRef}</span>}
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                  {activeTaskCount > 5 && (
                    <p className="text-[10px] text-gray-400 text-center pt-1">
                      +{activeTaskCount - 5} more tasks
                    </p>
                  )}
                </div>
              )}
            </>
          )}
        </section>

        <hr className="border-gray-200 cursor-pointer" onClick={() => setIsOpen(false)} />

        {/* SECTION 3: Service Ticket Counts */}
        <section>
          <button
            onClick={() => toggleSection("serviceTickets")}
            className="flex items-center gap-2 mb-3 w-full hover:opacity-70 transition-opacity"
          >
            <ChevronDown
              size={14}
              className={`text-gray-400 transition-transform ${collapsedSections.has("serviceTickets") ? "-rotate-90" : ""}`}
            />
            <Wrench size={16} className="text-purple-600" />
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Service Tickets
            </h3>
          </button>

          {!collapsedSections.has("serviceTickets") && (
            <div className="space-y-1.5">
              <StatusCountRow
                label="Estimated"
                count={stCounts.estimated}
                color="bg-gray-400"
              />
              <StatusCountRow
                label="Scheduled"
                count={stCounts.scheduled}
                color="bg-blue-500"
              />
              <StatusCountRow
                label="In Progress"
                count={stCounts.inProgress}
                color="bg-amber-500"
              />
              <StatusCountRow
                label="Completed (5 days)"
                count={stCounts.completed}
                color="bg-green-500"
              />
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function BookingItem({
  booking,
  isActive,
}: {
  booking: SidebarBooking;
  isActive: boolean;
}) {
  return (
    <div>
      <Link
        to={`/bookings/${booking.id}`}
        className={`flex items-center gap-1.5 px-2 py-1.5 rounded text-xs transition-colors group ${
          isActive
            ? "bg-blue-100 text-blue-700"
            : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
        }`}
      >
        <StatusDot status={booking.status} />
        <div className="flex-1 min-w-0">
          <span className="block truncate font-medium">
            {booking.clientName ?? booking.referenceNumber}
          </span>
          <span className="block truncate text-[10px] text-gray-400">
            {booking.referenceNumber}
          </span>
        </div>
        <ChevronRight
          size={12}
          className="opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
        />
      </Link>

      {/* Open service tickets under booking */}
      {booking.openServiceTickets.length > 0 && (
        <div className="ml-5 mt-0.5 space-y-0.5">
          {booking.openServiceTickets.map((st) => (
            <div
              key={st.id}
              className="px-1.5 py-0.5 text-[10px] text-gray-400"
            >
              <div className="flex items-center gap-1.5">
                <StatusDot status={st.status} />
                <span className="truncate">
                  {st.serial} - {st.serviceType}
                </span>
              </div>
              {st.startDatetime && (
                <div className="ml-3 text-gray-400/70">
                  {displayDateTime(st.startDatetime, "ddd MMM D, h:mm A")}
                  {" "}
                  <span className="text-gray-400/50">
                    {new Intl.DateTimeFormat("en-US", { timeZoneName: "short" })
                      .formatToParts(new Date(st.startDatetime))
                      .find((p) => p.type === "timeZoneName")?.value ?? ""}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatusDot({ status }: { status: JobStatus }) {
  const color =
    status === JobStatus.IN_PROGRESS
      ? "bg-amber-400"
      : status === JobStatus.SCHEDULED
      ? "bg-blue-400"
      : status === JobStatus.ESTIMATED
      ? "bg-gray-400"
      : "bg-gray-300";

  return <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${color}`} />;
}

function TaskStatusIcon({ status }: { status: TaskStatus }) {
  if (status === TaskStatus.IN_PROGRESS) {
    return <Clock size={12} className="text-amber-500 mt-0.5 shrink-0" />;
  }
  return <AlertCircle size={12} className="text-gray-400 mt-0.5 shrink-0" />;
}

function StatusCountRow({
  label,
  count,
  color,
}: {
  label: string;
  count: number;
  color: string;
}) {
  return (
    <div className="flex items-center justify-between px-2 py-1.5 rounded text-xs bg-white border border-gray-100">
      <div className="flex items-center gap-2">
        <span className={`w-2 h-2 rounded-full ${color}`} />
        <span className="text-gray-600">{label}</span>
      </div>
      <span className="font-semibold text-gray-800">{count}</span>
    </div>
  );
}
